import { inject } from "ui-common/di";
import type { IWritableCreatorProfileDataState, ICreatorProfileDataService } from "./types";
import type { IRpcService } from "ui-common/services/rpc/types";
import type { IAuthProvider } from "ui-common/services/auth/types";
import type { ProfileDataResult } from "api-contracts/rpc/profile-data";
import type { SaveProfileParams } from "api-contracts/rpc/save-profile";
import type { UpdateProfileParams } from "api-contracts/rpc/update-profile";
import type { DeleteUserParams } from "api-contracts/rpc/delete-user";
import type { sendChangeEmailVerificationParams } from "api-contracts/rpc/send-change-email-verification";

export class CreatorProfileDataService implements ICreatorProfileDataService {
  @inject<IWritableCreatorProfileDataState>("writableCreatorProfileDataState")
  private state!: IWritableCreatorProfileDataState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @inject<IAuthProvider>("authProvider")
  private authProvider!: IAuthProvider;

  private pendingLoad: Promise<void> | undefined;

  loadProfile(): void {
    if (!this.authProvider.isAuthenticated()) return;
    if (this.pendingLoad) return;

    this.state.loading.set(true);
    this.pendingLoad = this.doLoad();
  }

  reloadProfile(): void {
    this.pendingLoad = undefined;
    this.state.loading.set(true);
    this.pendingLoad = this.doLoad();
  }

  private async doLoad(): Promise<void> {
    try {
      const result = await this.rpcService.call<ProfileDataResult>("profileData", undefined);
      this.state.profile.set(result);
    } catch (error) {
      console.error("[CreatorProfileDataService] Error loading profile:", error);
    } finally {
      this.state.loading.set(false);
      this.pendingLoad = undefined;
    }
  }

  async saveProfile(params: SaveProfileParams): Promise<void> {
    await this.rpcService.call("saveProfile", params);
    await this.doLoad();
  }

  async updateProfile(params: UpdateProfileParams): Promise<void> {
    await this.rpcService.call("updateProfile", params);
    await this.doLoad();
  }

  async sendChangeEmailVerification(params: sendChangeEmailVerificationParams): Promise<void> {
    await this.rpcService.call("send-change-email-verification", params);
  }

  async deleteUser(params: DeleteUserParams): Promise<void> {
    await this.rpcService.call("deleteUser", params);
  }
}
